/**
 * MemberPermissionsDialog: the owner picks what else one member can do
 * (TRA-1700), such as reading the audit log. Opened from the member's
 * row; mounted only while open, so it always starts from what the member
 * holds now. Save sends the whole set, and it refuses to close mid-flight.
 */

import { useState, type FormEvent } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { PERMISSION_COPY, TEAM_PERMISSIONS, type TeamPermission } from '@/lib/teamPermissions'

interface MemberPermissionsDialogProps {
  displayName: string
  held: readonly TeamPermission[]
  isPending: boolean
  error: string | null
  onSubmit: (permissions: TeamPermission[]) => void
  onClose: () => void
}

export function MemberPermissionsDialog({
  displayName,
  held,
  isPending,
  error,
  onSubmit,
  onClose,
}: MemberPermissionsDialogProps): React.JSX.Element {
  const [chosen, setChosen] = useState<ReadonlySet<TeamPermission>>(new Set(held))
  const changed = chosen.size !== held.length || held.some((p) => !chosen.has(p))

  const toggle = (permission: TeamPermission): void => {
    const next = new Set(chosen)
    if (next.has(permission)) next.delete(permission)
    else next.add(permission)
    setChosen(next)
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!changed || isPending) return
    onSubmit(TEAM_PERMISSIONS.filter((p) => chosen.has(p)))
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Permissions</DialogTitle>
          <DialogDescription>
            Choose what else {displayName} can do. Without these, only the owner can.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <ul className="space-y-3">
            {TEAM_PERMISSIONS.map((permission) => {
              const id = `permission-${permission}`
              const copy = PERMISSION_COPY[permission]
              return (
                <li key={permission} className="flex items-start gap-3">
                  <Checkbox
                    id={id}
                    checked={chosen.has(permission)}
                    disabled={isPending}
                    aria-describedby={`${id}-hint`}
                    onCheckedChange={() => {
                      toggle(permission)
                    }}
                  />
                  <span className="flex flex-col gap-0.5">
                    <Label htmlFor={id}>{copy.label}</Label>
                    <span id={`${id}-hint`} className="text-xs text-muted-foreground">
                      {copy.hint}
                    </span>
                  </span>
                </li>
              )
            })}
          </ul>
          {error !== null && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !changed}>
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
